import {
  usePostResourceBookmark,
  useDeleteResourceBookmark,
  useDeleteAllResourceBookmarks,
} from '../../query/learning-api/use-resource';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../firebase';
import { useAuthContext } from '../../../auth';
import { useToast } from '../use-toast';
import { ResourceBookmarkToast } from '../../../components/toast/resource-table/resource-bookmark-toast';
import { APP_TEXT } from '../../../const/app-text';

export const useBookmarks = () => {
  const { userId } = useAuthContext();
  const { showSuccess } = useToast();

  const { postResourceBookmark } = usePostResourceBookmark();
  const bookmarkResource = async (resourceId: string) => {
    try {
      await postResourceBookmark(resourceId);

      logEvent(analytics, 'bookmark_learning_resource', {
        resource_id: resourceId,
        user_id: userId,
      });

      return {
        success: true,
      };
    } catch (err) {
      console.error('Failed to bookmark learning resource', err);
      return {
        success: false,
      };
    }
  };

  const { deleteResourceBookmark } = useDeleteResourceBookmark();
  const deleteBookmark = async (resourceId: string) => {
    try {
      await deleteResourceBookmark(resourceId);

      logEvent(analytics, 'delete_resource_bookmark', {
        resource_id: resourceId,
        user_id: userId,
      });

      return {
        success: true,
      };
    } catch (error) {
      console.error('Failed to remove bookmarked learning resource', error);
      return {
        success: false,
      };
    }
  };

  const { deleteAllResourceBookmarks } = useDeleteAllResourceBookmarks();

  const deleteAllBookmarks = async () => {
    try {
      await deleteAllResourceBookmarks();

      logEvent(analytics, 'delete_all_resource_bookmarks', {
        user_id: userId,
      });

      return {
        success: true,
      };
    } catch (error) {
      console.error('Failed to remove all bookmarked learning resources', error);
      return {
        success: false,
      };
    }
  };

  const toastHandler = (resourceId: string, isBookmarked: boolean, resourceTitle: string) => {
    const messageText =
      isBookmarked === true
        ? APP_TEXT.components.toast.bookmarking.bookmarkCreatedMessage
        : APP_TEXT.components.toast.bookmarking.bookmarkDeletedMessage;

    showSuccess({
      content: () => (
        <ResourceBookmarkToast
          resourceId={resourceId}
          isBookmarked={isBookmarked}
          text={{
            resourceTitle: resourceTitle,
            messageText: messageText,
          }}
        />
      ),
    });
  };

  const handleCreateBookmark = async (
    resourceId: string,
    resourceTitle: string,
    isBookmarked: boolean
  ) => {
    const result = await bookmarkResource(resourceId);
    if (result.success) {
      toastHandler(resourceId, !isBookmarked, resourceTitle);
    }
  };

  const handleDeleteBookmark = async (
    resourceId: string,
    resourceTitle: string,
    isBookmarked: boolean
  ) => {
    const result = await deleteBookmark(resourceId);
    if (result.success) {
      toastHandler(resourceId, !isBookmarked, resourceTitle);
    }
  };

  const handleDeleteAllBookmarks = async () => {
    await deleteAllBookmarks();
  };

  return {
    handleCreateBookmark,
    handleDeleteAllBookmarks,
    handleDeleteBookmark,
    bookmarkResource,
    deleteBookmark,
  };
};
